import { useTraduccion } from '@tallerya/i18n';
import {
  AvisoError,
  Boton,
  CampoAreaTexto,
  CampoSeleccion,
  CampoTexto,
  Fila,
  Seccion,
  useAvisos,
  useFormulario,
  Ventana,
} from '@tallerya/ui';
import { useCallback, useState } from 'react';
import { SelectorDeCliente } from '../activos/SelectorDeCliente';
import { useAsignables, useCambiosOrden, useClaveDeReintento, version, type CabeceraOrden } from './datos';
import { descripcionDe, ElegirVehiculo, type VehiculoElegido } from './ElegirVehiculo';

// Editar la cabecera de la orden (#131): lo reportado, las notas, la entrega
// prometida, la referencia del ERP, el cliente, el carro y a quién se asigna.
//
// En una ventana sobre la ficha y no en otra página: la ficha es donde se
// trabaja la orden, y salir de ella para corregir una fecha es un viaje de más.
//
// Manda **la cabecera entera** (ver `UpdateServiceOrderCommand`), con la versión
// que se leyó: si otra persona guardó mientras tanto, la API lo dice y no se pisa.

export function EditarCabecera({
  orden,
  abierta,
  onCerrar,
  onGuardada,
}: {
  orden: CabeceraOrden;
  abierta: boolean;
  onCerrar: () => void;
  /** Tras guardar bien: la ficha quita el aviso de lo que faltaba, que ya puede no faltar. */
  onGuardada: () => void;
}) {
  // Cada vez que se abre arranca de nuevo, con lo que la ficha muestra en ese
  // momento. Mientras está abierta **no se reinicia** aunque la ficha de atrás se
  // recargue: perdería lo escrito y el aviso de que otra persona guardó.
  if (!abierta) return null;
  return <Contenido key={String(orden.id)} orden={orden} onCerrar={onCerrar} onGuardada={onGuardada} />;
}

interface Valores {
  reportedIssue: string;
  notes: string;
  promisedAt: string;
  externalReference: string;
  customerId: string;
  assetId: string;
  assignedTo: string;
}

/** Un instante de la API a lo que espera `datetime-local`: la hora de quien mira, sin zona. */
function aLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

function vehiculoDe(orden: CabeceraOrden): VehiculoElegido | null {
  if (!orden.assetId) return null;
  return {
    id: String(orden.assetId),
    identificador: orden.assetIdentifier ?? '',
    descripcion:
      descripcionDe({ brandName: orden.assetBrandName, modelName: orden.assetModelName, year: orden.assetYear }) ||
      (orden.assetTypeName ?? ''),
    clienteId: orden.customerId ? String(orden.customerId) : null,
    clienteNombre: orden.customerName,
  };
}

function Contenido({
  orden: actual,
  onCerrar,
  onGuardada,
}: {
  orden: CabeceraOrden;
  onCerrar: () => void;
  onGuardada: () => void;
}) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosOrden();
  const claveDe = useClaveDeReintento();
  // La orden como estaba al abrir la ventana, con **su** versión: si otra persona
  // guarda mientras tanto, la API lo dice con un 409 en vez de pisarla.
  const [orden] = useState(actual);
  const asignables = useAsignables(String(orden.locationId));

  const [vehiculo, setVehiculo] = useState<VehiculoElegido | null>(vehiculoDe(orden));
  const [nombreCliente, setNombreCliente] = useState<string | null>(orden.customerName);

  const form = useFormulario<Valores>({
    inicial: {
      reportedIssue: orden.reportedIssue ?? '',
      notes: orden.notes ?? '',
      promisedAt: aLocal(orden.promisedAt),
      externalReference: orden.externalReference ?? '',
      customerId: orden.customerId ? String(orden.customerId) : '',
      assetId: orden.assetId ? String(orden.assetId) : '',
      assignedTo: orden.assignedTo ? String(orden.assignedTo) : '',
    },
    validar: (v) =>
      !v.assetId && !v.customerId ? { customerId: 'service_order.sin_cliente_ni_activo' } : {},
    guardar: (v) => {
      const id = String(orden.id);
      const versionLeida = version(orden.version);
      return cambios.editar(claveDe({ id, versionLeida, ...v }), id, versionLeida, v);
    },
    camposPorCodigo: {
      'service_order.activo_no_encontrado': 'assetId',
      'service_order.cliente_no_encontrado': 'customerId',
      'service_order.personal_no_encontrado': 'assignedTo',
      'service_order.personal_apagado': 'assignedTo',
    },
  });
  const { cambiar } = form;

  // Un carro con dueño trae a su cliente **solo si la orden no tenía**: cambiar el
  // carro no tiene por qué cambiar a quién se le cobra.
  const elegir = useCallback(
    (elegido: VehiculoElegido) => {
      setVehiculo(elegido);
      cambiar('assetId', elegido.id);
      if (!form.valores.customerId && elegido.clienteId) {
        cambiar('customerId', elegido.clienteId);
        setNombreCliente(elegido.clienteNombre);
      }
    },
    [cambiar, form.valores.customerId],
  );

  // El asignado de antes se ofrece aunque ya no cubra la sucursal: la orden ya era suya.
  const opcionesAsignado = [
    ...(asignables.data ?? []).map((p) => ({ valor: String(p.id), etiqueta: p.name })),
    ...(orden.assignedTo && !(asignables.data ?? []).some((p) => p.id === orden.assignedTo)
      ? [{ valor: String(orden.assignedTo), etiqueta: orden.assignedToName ?? '' }]
      : []),
  ];

  async function guardar() {
    if (await form.enviar()) {
      avisar({ titulo: t('admin.ordenes.aviso.guardada') });
      onGuardada();
      onCerrar();
    }
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.ordenes.editar.titulo')}
      ancho={720}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {form.guardando ? t('ui.formulario.guardando') : t('admin.ordenes.guardarCambios')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}

      <Seccion titulo={t('admin.ordenes.seccion.vehiculo')}>
        <ElegirVehiculo
          elegido={vehiculo}
          onElegir={elegir}
          onQuitar={() => {
            setVehiculo(null);
            cambiar('assetId', '');
          }}
          error={form.errores.assetId}
          id={form.idDe('assetId')}
        />
      </Seccion>

      <Seccion titulo={t('admin.ordenes.seccion.cliente')}>
        <SelectorDeCliente
          valor={form.valores.customerId}
          nombre={nombreCliente}
          error={form.errores.customerId}
          id={form.idDe('customerId')}
          onElegir={(id, nombre) => {
            cambiar('customerId', id);
            setNombreCliente(nombre);
          }}
          onQuitar={() => {
            cambiar('customerId', '');
            setNombreCliente(null);
          }}
        />
      </Seccion>

      <Seccion titulo={t('admin.ordenes.seccion.trabajo')}>
        <Fila>
          <CampoSeleccion
            form={form}
            nombre="assignedTo"
            etiqueta={t('admin.ordenes.asignarA')}
            placeholder={t('admin.ordenes.sinAsignar')}
            opciones={opcionesAsignado}
          />
          <CampoTexto form={form} nombre="promisedAt" etiqueta={t('admin.ordenes.entregaPrometida')} tipo="datetime-local" />
        </Fila>
        <CampoAreaTexto form={form} nombre="reportedIssue" etiqueta={t('admin.ordenes.fallaReportada')} ancho="completo" />
        <CampoAreaTexto form={form} nombre="notes" etiqueta={t('admin.ordenes.notas')} ancho="completo" />
        <CampoTexto
          form={form}
          nombre="externalReference"
          etiqueta={t('admin.ordenes.referenciaExterna')}
          info={t('admin.ordenes.referenciaExterna.info')}
          autoComplete="off"
        />
      </Seccion>
    </Ventana>
  );
}
